import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '../../utils/api';
import { toast } from 'react-toastify';
import moment from 'moment';
import PageHeader1 from '../../components/common/PageHeader1';
import * as XLSX from 'xlsx';

const AttendanceScreen = () => {
    const navigate = useNavigate();
    const [drivers, setDrivers] = useState([]);
    const [pins, setPins] = useState({});
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        fetchDrivers();
    }, []);

    const fetchDrivers = async () => {
        try {
            const token = localStorage.getItem('auth_token');
            const response = await apiClient.get('accounts/drivers', {
                headers: { Authorization: `Bearer ${token}` }
            });
            const driversData = response.data.data;
            
            // Fetch status for each driver
            const driversWithStatus = await Promise.all(
                driversData.map(async (driver) => {
                    const statusResponse = await apiClient.get(`accounts/attendance/driver/${driver.id}`, {
                        headers: { Authorization: `Bearer ${token}` }
                    });
                    return {
                        ...driver,
                        status: statusResponse.data.status,
                        attendance: statusResponse.data.attendance
                    };
                })
            );
            
            setDrivers(driversWithStatus);
        } catch (error) {
            console.error('Error:', error);
            toast.error('Failed to fetch drivers');
        }
    };

    const handleAttendance = async (driver) => {
        const pin = pins[driver.id];
        if (!pin) {
            toast.error('Please enter PIN');
            return;
        }

        setLoading(true);
        try {
            const token = localStorage.getItem('auth_token');
            const isCheckedIn = driver.status === 'check-in';
            const endpoint = `accounts/attendance/${isCheckedIn ? 'checkout' : 'checkin'}`;
            
            await apiClient.post(endpoint, {
                driver_id: driver.id,
                pin: pin
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });

            // Clear PIN and refresh drivers
            setPins(prev => ({ ...prev, [driver.id]: '' }));
            await fetchDrivers();
            
            toast.success(`${isCheckedIn ? 'Check-out' : 'Check-in'} successful`);
        } catch (error) {
            console.error('Error:', error);
            // Show the specific error message from the backend
            const errorMessage = error.response?.data?.message || 'Operation failed';
            toast.error(errorMessage);
            
            // Clear PIN on invalid PIN error
            if (errorMessage === 'Invalid PIN') {
                setPins(prev => ({ ...prev, [driver.id]: '' }));
            }
        } finally {
            setLoading(false);
        }
    };

    const exportToExcel = () => {
        // Prepare data for export
        const exportData = drivers.map((driver, index) => ({
            'Id': driver.id,
            'Driver Name': driver.name,
            'Status': driver.status === 'check-in' ? 'Checked In' : 'Checked Out',
            'Last Check In/Out': driver.attendance ? moment(driver.attendance.created_at).format('DD/MM/YYYY HH:mm:ss') : '-'
        }));

        // Create workbook and worksheet
        const wb = XLSX.utils.book_new();
        const ws = XLSX.utils.json_to_sheet(exportData);

        // Set column widths
        ws['!cols'] = [
            { wch: 10 },  // Id
            { wch: 25 },  // Driver Name
            { wch: 15 },  // Status
            { wch: 25 }   // Last Check In/Out
        ];

        // Add worksheet to workbook
        XLSX.utils.book_append_sheet(wb, ws, 'Attendance');

        // Generate filename with current date
        const filename = `Attendance_${moment().format('YYYY-MM-DD_HHmmss')}.xlsx`;

        // Save file
        XLSX.writeFile(wb, filename);
        toast.success('Attendance data exported successfully');
    };

    return (
        <div className="body d-flex py-3">
            <div className="container-xxl">
                <PageHeader1 
                    pagetitle='Driver Attendance' 
                    righttitle='Refresh'
                    buttonText='Refresh'
                    onButtonClick={fetchDrivers}
                    permission="attendance.view"
                />
                <div className="row g-3 mb-3">
                    <div className="col-md-12">
                        <div className="card">
                            <div className="card-header d-flex justify-content-between align-items-center">
                                <h6 className="mb-0">Attendance Records</h6>
                                <button 
                                    className="btn btn-success"
                                    onClick={exportToExcel}
                                    disabled={drivers.length === 0}
                                >
                                    <i className="icofont-file-excel me-2"></i>
                                    Export to Excel
                                </button>
                            </div>
                            <div className="card-body">
                                <div className="table-responsive">
                                    <table className="table table-hover align-middle mb-0">
                                        <thead>
                                            <tr>
                                                <th>Id</th>
                                                <th>Driver Name</th>
                                                <th>Status</th>
                                                <th>Last Check In/Out</th>
                                                <th>PIN</th>
                                                <th>Action</th>
                                                <th>History</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {drivers.map((driver) => (
                                                <tr key={driver.id}>
                                                    <td>#{driver.id}</td>
                                                    <td>{driver.name}</td>
                                                    <td>
                                                        <span className={`badge ${driver.status === 'check-in' ? 'bg-success' : 'bg-warning'}`}>
                                                            {driver.status === 'check-in' ? 'Checked In' : 'Checked Out'}
                                                        </span>
                                                    </td>
                                                    <td>{driver.attendance ? moment(driver.attendance.created_at).format('DD/MM/YYYY HH:mm:ss') : '-'}</td>
                                                    <td>
                                                        <input
                                                            type="password"
                                                            className="form-control"
                                                            placeholder="Enter PIN"
                                                            value={pins[driver.id] || ''}
                                                            onChange={(e) => setPins(prev => ({ ...prev, [driver.id]: e.target.value }))}
                                                        />
                                                    </td>
                                                    <td>
                                                        <button
                                                            className={`btn ${driver.status === 'check-in' ? 'btn-warning' : 'btn-success'}`}
                                                            onClick={() => handleAttendance(driver)}
                                                            disabled={loading}
                                                        >
                                                            {driver.status === 'check-in' ? 'Check Out' : 'Check In'}
                                                        </button>
                                                    </td>
                                                    <td>
                                                        <button
                                                            className="btn btn-info"                                            
                                                            onClick={() => navigate(`/accounts/attendance/history/${driver.id}`)}
                                                        >
                                                            History
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AttendanceScreen;
